/**
 * Fonctions pour créer les différents types de nœuds
 */
import React from 'react';
import { Position } from '@xyflow/react';
import { ChatFlowStartLabel, GreetingLabel, NodeLabel } from '../components/NodeLabel';
import { getColor } from './nodeUtils';

/**
 * Crée le nœud de début de ChatFlow
 */
export const createChatFlowStartNode = (x, y, onEdit) => {
  return {
    id: 'chatflow-start',
    type: 'default',
    position: { x: x + 30, y },
    data: {
      label: <ChatFlowStartLabel onEdit={onEdit} />
    },
    sourcePosition: Position.Bottom,
    style: {
      background: '#fff',
      border: '2px solid #5b5fc7',
      borderRadius: '50%',
      width: '140px',
      height: '140px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    connectable: { target: false },
  };
};

/**
 * Crée le nœud Greeting
 */
export const createGreetingNode = (x, y, enabledMessagesCount, onEdit, onDelete) => {
  return {
    id: 'greeting',
    type: 'default',
    position: { x, y },
    data: {
      label: <GreetingLabel 
        enabledMessagesCount={enabledMessagesCount}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: '#fff',
      border: '2px solid #10b981',
      borderRadius: '10px',
      width: 200,
      boxShadow: '0 4px 12px rgba(16,185,129,0.15)',
    },
  };
};

/**
 * Détecte le vrai type d'un routing basé sur son contenu
 */
const detectRoutingType = (routingItem) => {
  // Si customerChoiceActions existe, c'est un Customer Choice (même si type dit autre chose)
  if (routingItem.customerChoiceActions && Object.keys(routingItem.customerChoiceActions).length > 0) {
    return 'customerChoice';
  }
  // Sinon, utiliser le type déclaré
  return routingItem.type?.toLowerCase() || 'queue';
};

/**
 * Crée un nœud de routing (Queue, Customer Choice, Form)
 */
export const createRoutingNode = (key, routingItem, x, y, onEdit, onDelete, chatFlowData) => {
  const nodeId = `routing-${key}`;
  const showDelete = key !== "0"; // Ne pas afficher le bouton supprimer pour le routing "0"
  
  // Détecter le vrai type
  const actualType = detectRoutingType(routingItem);
  
  // Déterminer le displayName selon le type
  let displayName;
  if (actualType === 'queue') {
    // Pour les queues, afficher le nom de la queue avec le nom du routing entre parenthèses
    const routingName = routingItem.displayName || (key === "0" ? "start" : key);
    displayName = `${routingItem.queueDisplayName || 'Queue'} (${routingName})`;
  } else {
    displayName = routingItem.displayName || routingItem.customerChoiceName || (key === "0" ? "start" : key);
  }
  
  return {
    node: {
      id: nodeId,
      type: 'default',
      position: { x, y },
      data: {
        label: <NodeLabel
          nodeId={nodeId}
          nodeType={actualType}
          displayName={displayName}
          onEdit={onEdit}
          onDelete={onDelete}
          showDelete={showDelete}
          customerChoiceActions={
            (actualType.toLowerCase() === 'customerchoice')
              ? routingItem.customerChoiceActions
              : null
          }
        />
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: {
        background: '#fff',
        border: `2px solid ${getColor(actualType)}`,
        borderRadius: '10px',
        width: 200,
        boxShadow: `0 4px 12px ${getColor(actualType)}33`,
      },
    }
  };
};

/**
 * Crée un nœud Queue temporaire (pas dans routing)
 */
export const createQueueNode = (queueId, actionKey, queueDisplayName, routingName, x, y, onEdit, onDelete) => {
  const nodeId = `queue-${queueId}${actionKey ? `-${actionKey}` : ''}`;
  
  return {
    id: nodeId,
    type: 'default',
    position: { x, y },
    data: {
      label: <NodeLabel
        nodeId={nodeId}
        nodeType="queue"
        displayName={`${queueDisplayName || 'Queue'} (${routingName === "0" ? "start" : routingName})`}
        onEdit={onEdit}
        onDelete={onDelete}
        showDelete={true}
      />
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: '#fff',
      border: '2px solid #3b82f6',
      borderRadius: '10px',
      width: 200,
      boxShadow: '0 4px 12px rgba(59,130,246,0.15)',
    },
  };
};

/**
 * Crée un edge standard
 */
export const createEdge = (id, source, target, options = {}) => {
  return {
    id,
    type: options.type || 'custom',
    source,
    target,
    sourceHandle: options.sourceHandle,
    targetHandle: options.targetHandle,
    animated: options.animated !== undefined ? options.animated : true,
    label: options.label,
    style: options.style || { stroke: '#5b5fc7', strokeWidth: 2 },
    markerEnd: options.markerEnd || { type: 'arrowclosed', color: '#5b5fc7' },
    data: options.data || {},
  };
};
